import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, Gauge, RefreshCw, RotateCcw } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

const hhmm = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const hourLabel = (h) => new Date(`${h}:00Z`).toLocaleTimeString('en-IN', { hour: 'numeric' });
const secs = (s) => (s >= 1 ? `${s.toFixed(1)} s` : `${Math.round(s * 1000)} ms`);

const TONE = { slate: '#0f172a', red: '#b91c1c', amber: '#b45309', emerald: '#047857' };
const RED = { color: TONE.red, fontWeight: 600 };
const AMBER = { color: TONE.amber, fontWeight: 600 };

const Stat = ({ icon: Icon, label, value, tone = 'slate', testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon size={13} /> {label}</p>
    <p className="mt-1 text-2xl font-bold" style={{ color: TONE[tone] }}>{value}</p>
  </div>
);

// Errors per hour as bars, requests as a faint background bar.
const HourChart = ({ hours }) => {
  const now = new Date();
  const slots = Array.from({ length: 24 }, (_, i) => {
    const d = new Date(now.getTime() - (23 - i) * 3600e3);
    const key = d.toISOString().slice(0, 13) + ':00';
    return hours.find((h) => h.hour === key) || { hour: key, requests: 0, errors: 0, slow: 0, stalls: 0 };
  });
  const maxBad = Math.max(1, ...slots.map((s) => s.errors + s.slow + s.stalls));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="health-hour-chart">
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <h2 className="text-sm font-bold text-slate-900">Problems per hour · last 24 hours</h2>
        <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500" /> Failed</span>
        <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" /> Very slow / paused</span>
      </div>
      <div className="flex h-40 items-end gap-1">
        {slots.map((s) => {
          const bad = s.slow + s.stalls;
          return (
            <div key={s.hour} className="group relative flex h-full flex-1 flex-col justify-end" data-testid={`health-bar-${s.hour}`}>
              <div className="w-full rounded-t bg-amber-400" style={{ height: `${(bad / maxBad) * 100}%` }} />
              <div className="w-full bg-red-500" style={{ height: `${(s.errors / maxBad) * 100}%` }} />
              {!s.errors && !bad && <div className="h-0.5 w-full rounded bg-emerald-300" />}
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[11px] text-white group-hover:block">
                {hourLabel(s.hour)} · {s.requests} requests · {s.errors} failed · {s.slow} slow · {s.stalls} paused
              </div>
            </div>);
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-slate-400"><span>{hourLabel(slots[0].hour)}</span><span>{hourLabel(slots[12].hour)}</span><span>now</span></div>
    </div>
  );
};

const PathTable = ({ title, rows, testid, empty }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <h2 className="mb-2 text-sm font-bold text-slate-900">{title}</h2>
    {!rows.length ? <p className="text-xs text-slate-500">{empty}</p> : (
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase text-slate-500"><tr><th className="py-1.5">Screen / API</th><th className="py-1.5 text-right">Requests</th><th className="py-1.5 text-right">Average</th><th className="py-1.5 text-right">Slowest</th><th className="py-1.5 text-right">Failed</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.path} className="border-t border-slate-100">
            <td className="max-w-[340px] truncate py-1.5 font-mono text-xs" title={r.path}>{r.path.replace(/^\/api/, '')}</td>
            <td className="py-1.5 text-right text-xs">{r.count || '—'}</td>
            <td className="py-1.5 text-right text-xs" style={r.avg_s >= 3 ? AMBER : undefined}>{r.count ? secs(r.avg_s) : '—'}</td>
            <td className="py-1.5 text-right text-xs" style={r.max_s >= 8 ? RED : r.max_s >= 3 ? AMBER : undefined}>{secs(r.max_s)}</td>
            <td className="py-1.5 text-right text-xs" style={r.errors ? RED : { color: '#94a3b8' }}>{r.errors}</td>
          </tr>))}</tbody>
      </table>)}
  </div>
);

export default function PlatformHealth() {
  const { authHeader } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setD((await axios.get(`${API}/platform/health`, { headers: authHeader() })).data); } catch { setD((x) => x || { hours: [], events: [], slowest: [], failing: [], now: {} }); } finally { setBusy(false); }
  }, [authHeader]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (!d) return <div className="p-6 text-sm text-slate-500" data-testid="health-loading">Loading…</div>;
  const sum = (k) => d.hours.reduce((a, h) => a + (h[k] || 0), 0);
  const restarts = d.events.filter((e) => e.kind === 'restart');
  const alerts = d.events.filter((e) => e.kind !== 'restart');
  const errs = sum('errors');
  return (
    <div className="min-h-full space-y-4 bg-slate-50 p-4 sm:p-6" data-testid="platform-health-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">App health</h1>
          <p className="text-sm text-slate-500">Last 24 hours of the app server. Refreshes every minute.</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${d.now.errors || d.now.slow ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid="health-now">
          {d.now.errors || d.now.slow ? `Last 5 min: ${d.now.errors} failed, ${d.now.slow} slow` : 'All good right now'}
        </span>
        <button type="button" onClick={load} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-500" data-testid="health-refresh"><RefreshCw size={13} className={busy ? 'animate-spin' : ''} /> Refresh</button>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat icon={Activity} label="Requests" value={sum('requests').toLocaleString('en-IN')} testid="health-stat-requests" />
        <Stat icon={AlertTriangle} label="Failed" value={errs} tone={errs ? 'red' : 'emerald'} testid="health-stat-errors" />
        <Stat icon={Gauge} label="Very slow / paused" value={sum('slow') + sum('stalls')} tone={sum('slow') + sum('stalls') ? 'amber' : 'emerald'} testid="health-stat-slow" />
        <Stat icon={RotateCcw} label="Server restarts" value={restarts.length} testid="health-stat-restarts" />
      </div>
      <HourChart hours={d.hours} />
      <div className="grid gap-4 xl:grid-cols-2">
        <PathTable title="Slowest screens" rows={d.slowest} testid="health-slowest" empty="No requests recorded yet." />
        <PathTable title="Screens with failures" rows={d.failing} testid="health-failing" empty="No failed requests in the last 24 hours." />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="health-events">
        <h2 className="mb-2 text-sm font-bold text-slate-900">Restarts and alerts</h2>
        {!d.events.length ? <p className="text-xs text-slate-500">No restarts or alerts in the last 24 hours.</p> : (
          <ul className="space-y-1 text-sm">{[...restarts, ...alerts].sort((a, b) => b.at.localeCompare(a.at)).map((e) => (
            <li key={`${e.kind}-${e.at}`} className="flex gap-3" data-testid={`health-event-${e.kind}`}>
              <span className="w-32 shrink-0 text-xs text-slate-500">{hhmm(e.at)}</span>
              <span className="text-xs font-semibold" style={{ color: e.kind === 'alert' ? TONE.red : e.kind === 'resolved' ? TONE.emerald : '#334155' }}>
                {e.kind === 'restart' ? 'Server started (publish or restart)' : e.kind === 'alert' ? `Alert emailed: ${e.errors} failed, ${e.slow} slow in 5 min` : 'Back to normal email sent'}
              </span>
            </li>))}</ul>)}
      </div>
    </div>
  );
}
